import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { Box, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import deepmerge from 'deepmerge';

import useNotification from '../../../../../core/hooks/use_notification';
import { ROUTE_EXPIRATION_PROMOTIONS } from '../../../../../core/constants';
import { getProductExpirationPromotionsAction, getProductExpirationPromotionsState, invalidateProductExpirationPromotions } from '../../../../../features/price/get_product_expiration_promotions';
import { getDefaultPaginationQueryValues } from '../../../../../core/utils/api_utils';
import ExpirationPromotionsFilters from './expiration_promotions_filters';
import { getPodsNotificationData, getPriceNotificationData, getProductBatchesNotificationData, getProductExpirationPromotionsNotificationData, getProductNotificationData } from '../../../../../core/utils/notification_utils';
import ExpirationPromotionsList from './expiration_promotions_list';
import PaginationControls from '../../../../common/pagination_controls';
import { getProductBatchesAction, getProductBatchesState } from '../../../../../features/inventory/get_product_batches';
import { getAllPodsAction, getAllPodsState } from '../../../../../features/pod/get_all_pods';
import PromotionsFormDialog from './form/promotions_form_dialog';
import DeletePromotionsFormDialog from './form/delete_promotions_form_dialog';
import { getPriceAction, getPriceState } from '../../../../../features/price/get_price';
import { getProductAction, getProductState } from '../../../../../features/product/get_product';
import PageHeader from '../../../../common/page_header';

const initialQueryParams = {
    ...getDefaultPaginationQueryValues,
    podId: '',
    productBatchId: ''
};

const ExpirationPromotionsDetailsPage = () => {
    const dispatch = useDispatch();
    const { showErrorMessage } = useNotification();
    const navigate = useNavigate();
    const { productId } = useParams();
    const location = useLocation();

    const getProduct = useSelector(getProductState);
    const getProductExpirationPromotions = useSelector(getProductExpirationPromotionsState);
    const getProductBatches = useSelector(getProductBatchesState);
    const getAllPods = useSelector(getAllPodsState);
    const getPrice = useSelector(getPriceState);

    const [queryParams, setQueryParams] = useState({ ...initialQueryParams, productId });

    const refreshList = useCallback(() => {
        dispatch(getProductExpirationPromotionsAction(queryParams))
            .then(response => showErrorMessage(getProductExpirationPromotionsNotificationData(response)));
    }, [dispatch, queryParams, showErrorMessage]);

    useEffect(() => {
        refreshList();

        return () => {
            dispatch(invalidateProductExpirationPromotions());
        };
    }, [dispatch, refreshList]);

    useEffect(() => {
        dispatch(getProductAction({ id: productId }))
            .then(respone => showErrorMessage(getProductNotificationData(respone)));
        dispatch(getProductBatchesAction(productId))
            .then(response => showErrorMessage(getProductBatchesNotificationData(response)));
        dispatch(getAllPodsAction())
            .then(response => showErrorMessage(getPodsNotificationData(response)));
        dispatch(getPriceAction(productId))
            .then(response => showErrorMessage(getPriceNotificationData(response)));
    }, [dispatch, productId, showErrorMessage]);

    const renderData = () => {
        if (getProductBatches?.loading || getAllPods?.loading || getPrice?.loading) {
            return (
                <Box mt={ 6 } width="100%">
                    <LinearProgress/>
                </Box>
            );
        }

        if (getProductBatches?.data && getAllPods?.data && getPrice?.data && getProduct?.data) {
            return (
                <>
                    <Paper variant="outlined" sx={ { p: 2, mb: 3, backgroundColor: 'slate.50' } }>
                        <Stack direction="row" spacing={ 1 } alignItems="center">
                            <Typography variant="body1" sx={ { fontWeight: 600 } }>Promocja na towary z krótką datą
                                przydatności:</Typography>
                            <Typography variant="body1">{ getProduct?.data.name }</Typography>
                        </Stack>
                    </Paper>

                    <Box sx={ {
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 2,
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 3
                    } }>
                        <Box sx={ {
                            flexGrow: 1,
                            minWidth: { xs: '100%', md: '500px' },
                            maxWidth: { xs: '100%', md: '60%' }
                        } }>
                            <ExpirationPromotionsFilters
                                onSelectPodChange={ podId => setQueryParams(prevState => deepmerge(prevState, {
                                    podId,
                                    page: 0
                                })) }
                                onSelectProductBatchIdChange={ productBatchId => setQueryParams(prevState => deepmerge(prevState, {
                                    productBatchId,
                                    page: 0
                                })) }
                            />
                        </Box>

                        <Stack direction="row" spacing={ 2 } sx={ { width: { xs: '100%', md: 'auto' } } }>
                            <PromotionsFormDialog
                                productId={ productId }
                                refreshList={ refreshList }
                            />
                            <DeletePromotionsFormDialog
                                productId={ productId }
                                refreshList={ refreshList }
                            />
                        </Stack>
                    </Box>

                    { getProductExpirationPromotions?.loading && <LinearProgress sx={ { mb: 2 } }/> }

                    {
                        (getProductExpirationPromotions?.data?.content) && (
                            <Box sx={ { display: 'flex', flexDirection: 'column', gap: 2 } }>
                                <ExpirationPromotionsList refreshList={ refreshList }/>
                                <Box sx={ { display: 'flex', justifyContent: 'center' } }>
                                    <PaginationControls
                                        currentPage={ getProductExpirationPromotions?.data?.pageable?.pageNumber }
                                        totalPages={ getProductExpirationPromotions?.data?.totalPages }
                                        setPage={ page => setQueryParams(prevState => deepmerge(prevState, { page })) }
                                    />
                                </Box>
                            </Box>
                        )
                    }
                </>
            );
        }

        return null;
    };

    return (
        <Box sx={ { display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 3, mb: 4 } }>
            <PageHeader
                title="Szczegóły promocji"
                showBack={ true }
                onBack={ () => navigate(ROUTE_EXPIRATION_PROMOTIONS, {
                    state: {
                        queryParams: location?.state?.queryParams,
                        tab: location?.state?.tab
                    }
                }) }
            />

            { renderData() }
        </Box>
    );
};

export default ExpirationPromotionsDetailsPage;